import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, catchError, of, throwError, timeout } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';

import { ConfigService } from '../shared/utils/http';
import { AdminSessionService } from './admin-session.service';
import { ToastService } from './toast.service';

export interface AdminUser {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  code?: string;
  full_name?: string;
}

export interface AdminLoginResponse {
  access: string;
  refresh: string;
  user: AdminUser;
}

export interface AdminRefreshResponse {
  access: string;
  refresh?: string;
}

export interface AdminCheckResponse {
  user: AdminUser;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly http: ConfigService;

  constructor(
    private readonly client: HttpClient,
    private readonly router: Router,
    private readonly spinner: NgxSpinnerService,
    private readonly adminSessionService: AdminSessionService,
    private readonly toastService: ToastService,
  ) {
    this.http = new ConfigService(client);
  }

  get adminAccessToken(): string | null {
    return this.adminSessionService.accessToken;
  }

  set adminAccessToken(token: string | null) {
    this.adminSessionService.accessToken = token;
  }

  get adminRefreshToken(): string | null {
    return this.adminSessionService.refreshToken;
  }

  set adminRefreshToken(token: string | null) {
    this.adminSessionService.refreshToken = token;
  }

  get adminUser(): AdminUser | null {
    return this.adminSessionService.user;
  }

  set adminUser(user: AdminUser | null) {
    this.adminSessionService.user = user;
  }

  get adminUserId(): number | null {
    return this.adminUser?.id ?? null;
  }

  get isAdminAuthenticated(): boolean {
    return this.adminSessionService.isAuthenticated;
  }

  async login(username: string, password: string, isPage: boolean = true): Promise<boolean> {
    const data = { username, password };
    this.spinner.show();

    try {
      const response = await firstValueFrom(
        this.http.post<AdminLoginResponse>('auth/login/', data).pipe(
          timeout(5000),
          catchError((error) => {
            if (error.name === 'TimeoutError') {
              this.toastService.showAdminError('La solicitud de login ha tardado demasiado. Intente nuevamente.');
            } else {
              this.toastService.showAdminError('Su usuario y contraseña no son correctos');
            }
            return throwError(() => error);
          }),
        ),
      );

      this.persistAdminSession(response);
      this.spinner.hide();

      if (isPage) {
        await this.router.navigateByUrl('/admin/plants');
      } else {
        await this.router.navigateByUrl('/');
      }

      return true;
    } catch (error) {
      this.spinner.hide();
      this.clearAdminSession();
      return false;
    }
  }

  async check(): Promise<boolean> {
    if (!this.adminAccessToken) {
      return false;
    }

    this.spinner.show();
    try {
      const response = await firstValueFrom(this.http.get<AdminCheckResponse>('auth/check/'));
      this.adminUser = response.user;
      this.spinner.hide();
      return true;
    } catch (error) {
      this.spinner.hide();
      this.clearAdminSession();
      return false;
    }
  }

  logout(): Observable<unknown> {
    const refresh = this.adminRefreshToken;
    this.clearAdminSession();
    if (!refresh) {
      return of({});
    }

    return this.http.post('auth/logout/', { refresh });
  }

  persistAdminSession(response: AdminLoginResponse | AdminRefreshResponse, user?: AdminUser | null): void {
    if ('user' in response && response.user) {
      this.adminSessionService.persistLoginSession(response);
    } else if (user !== undefined) {
      this.adminSessionService.persistRefreshSession(response);
      this.adminSessionService.rehydrateUser(user);
    } else {
      this.adminSessionService.persistRefreshSession(response);
    }
  }

  clearAdminSession(): void {
    this.adminSessionService.clearSession();
  }

  restar_psw(data: { id: number }) {
    return this.http.get(`user/${data.id}/psw/`);
  }

  update_psw(id: number, data: unknown) {
    return this.http.put(`user/${id}/psw/update/`, data);
  }

  put_user(data: { id: number }) {
    return this.http.put(`user/${data.id}/`, data);
  }

  post_user(data: unknown) {
    return this.http.post('user/', data);
  }

  create_update(form: { id?: number }) {
    if (form.id) {
      return this.put_user(form as { id: number });
    }
    return this.post_user(form);
  }
}
